'use client';

import { Button } from '@govinity/ui/components/ui/button';
import { MeetingStatus, resolveMeetingConfig } from '@govinity/validation';
import type { MeetingEventPublicGetResponse } from '@govinity/validation/public';
import { useQuery } from '@tanstack/react-query';
import { Mic } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import api from '@/lib/api';
import { getLoginUrlFromClient } from '@/lib/utils/login-url';
import { useAuthStore } from '@/stores/authStore';
import {
  hasAvailableEntry,
  hasSelectableEntry,
  publicSpeakerOptionsQueryKey,
  toEntryKey,
} from './helpers';
import { SignupDialog } from './signup-dialog';
import { useUserSignups } from './use-user-signups';
import { useGovernmentContext } from '../../../context';

type PublicSpeakerSignupProps = {
  meeting: Pick<MeetingEventPublicGetResponse, 'id' | 'status' | 'agenda'>;
};

export function PublicSpeakerSignup({ meeting }: PublicSpeakerSignupProps) {
  const t = useTranslations('PortalPublicSpeakerSignup');
  const { government } = useGovernmentContext();
  const user = useAuthStore((state) => state.user);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { publicSpeakers } = resolveMeetingConfig(government.meetingConfig);
  const isSignupPossible =
    publicSpeakers.enabled &&
    meeting.status === MeetingStatus.Upcoming &&
    !!meeting.agenda?.publishedAt;

  const { data } = useQuery({
    enabled: isSignupPossible,
    queryKey: publicSpeakerOptionsQueryKey(government.code, meeting.id),
    queryFn: () => api.governments.publicSpeakers(government.code).getById(meeting.id),
    retry: false,
  });

  const { data: signups } = useUserSignups(government.code, meeting.id, isSignupPossible && !!user);

  const sections = data?.sections ?? [];

  if (!isSignupPossible || !hasSelectableEntry(sections)) {
    return null;
  }

  // Nothing left to sign up for, so the dialog would open empty — the meeting page card is where
  // the citizen manages the sign-ups they already have.
  const signedUpKeys = new Set(
    (signups?.records ?? []).map(({ section, type }) => toEntryKey({ id: section.id, type }))
  );

  if (user && !hasAvailableEntry(sections, signedUpKeys)) {
    return null;
  }

  if (!user) {
    return (
      <Button asChild size="sm" tooltip={{ content: t('loginRequiredTooltip') }} variant="outline">
        <a href={getLoginUrlFromClient()} rel="noreferrer">
          <Mic aria-hidden="true" />
          {t('signUpButton')}
        </a>
      </Button>
    );
  }

  return (
    <>
      <Button onClick={() => setIsDialogOpen(true)} size="sm" variant="outline">
        <Mic aria-hidden="true" />
        {t('signUpButton')}
      </Button>
      {isDialogOpen && <SignupDialog onOpenChange={setIsDialogOpen} />}
    </>
  );
}
